/* Explanation screen for the Feed → Queue → Backlog content pipeline.
   Visualizes the three-bucket system with an animated flow diagram. */

import {
  Inbox,
  ListChecks,
  BookmarkIcon,
  ArrowDown,
  Sparkles,
  Eye,
  CheckCircle2,
  XCircle,
  Pin,
  Search,
  ArrowRight,
} from "lucide-react";
import {
  FeatureInfoDialog,
  InfoSection,
  InfoSectionTitle,
  InfoStep,
  InfoHeroBadge,
} from "./FeatureInfoDialog";

interface ContentFlowInfoProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function FlowDiagram() {
  return (
    <div className="flex flex-col items-center gap-0 my-2">
      {/* Feed bucket */}
      <div className="w-full max-w-[260px] rounded-xl border-2 border-chart-1/40 bg-chart-1/8 p-3 text-center relative">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Inbox className="size-4 text-chart-1" />
          <span className="text-sm font-bold text-chart-1">Feed</span>
        </div>
        <p className="text-[10px] text-muted-foreground">
          New items from your sources land here
        </p>
      </div>

      {/* Arrow + labels */}
      <div className="flex items-center gap-6 py-1.5">
        <span className="text-[9px] text-chart-3 font-semibold flex items-center gap-0.5">
          <ArrowDown className="size-3" /> Queue
        </span>
        <span className="text-[9px] text-chart-2 font-semibold flex items-center gap-0.5">
          <ArrowDown className="size-3" /> Backlog
        </span>
        <span className="text-[9px] text-destructive/70 font-semibold flex items-center gap-0.5">
          <XCircle className="size-3" /> Dismiss
        </span>
      </div>

      {/* Queue + Backlog side by side */}
      <div className="flex gap-2.5 w-full max-w-[280px]">
        <div className="flex-1 rounded-xl border-2 border-chart-3/40 bg-chart-3/8 p-2.5 text-center">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <ListChecks className="size-3.5 text-chart-3" />
            <span className="text-xs font-bold text-chart-3">Queue</span>
          </div>
          <p className="text-[9px] text-muted-foreground leading-tight">
            Your daily plan
          </p>
        </div>
        <div className="flex-1 rounded-xl border-2 border-chart-2/40 bg-chart-2/8 p-2.5 text-center">
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <BookmarkIcon className="size-3.5 text-chart-2" />
            <span className="text-xs font-bold text-chart-2">Backlog</span>
          </div>
          <p className="text-[9px] text-muted-foreground leading-tight">
            Saved for later
          </p>
        </div>
      </div>

      {/* Completion */}
      <div className="flex items-center gap-1.5 py-1.5">
        <ArrowDown className="size-3 text-primary/40" />
      </div>
      <div className="rounded-xl border-2 border-primary/30 bg-primary/8 px-4 py-2 text-center">
        <div className="flex items-center justify-center gap-1.5">
          <CheckCircle2 className="size-3.5 text-primary" />
          <span className="text-xs font-bold text-primary">Completed</span>
        </div>
      </div>
    </div>
  );
}

export function ContentFlowInfo({ open, onOpenChange }: ContentFlowInfoProps) {
  return (
    <FeatureInfoDialog
      open={open}
      onOpenChange={onOpenChange}
      title="How Content Flows"
    >
      <div className="space-y-5">
        {/* Hero */}
        <div className="text-center space-y-3">
          <InfoHeroBadge>
            <Sparkles className="size-3" />
            Core Workflow
          </InfoHeroBadge>
          <h2 className="text-xl font-bold tracking-tight">
            Feed, Queue & Backlog
          </h2>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
            Every piece of content flows through three areas. Understanding this
            pipeline is the key to using FeedKiwi effectively.
          </p>
        </div>

        {/* Flow diagram */}
        <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
          <FlowDiagram />
        </div>

        {/* The Three Buckets */}
        <InfoSection>
          <InfoSectionTitle
            icon={<Inbox className="size-4" />}
            iconColor="text-chart-1"
            iconBg="bg-chart-1/12"
          >
            The Feed — Your Inbox
          </InfoSectionTitle>
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            When you tap "Update Feed," FeedKiwi checks all your sources for new
            content and drops it here in reverse chronological order. Items from
            starred sources appear at the top.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {["Review items", "Filter by type/tag", "Starred items first"].map(
              (t) => (
                <span
                  key={t}
                  className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-chart-1/10 text-chart-1 border border-chart-1/20"
                >
                  {t}
                </span>
              ),
            )}
          </div>
        </InfoSection>

        <InfoSection>
          <InfoSectionTitle
            icon={<ListChecks className="size-4" />}
            iconColor="text-chart-3"
            iconBg="bg-chart-3/12"
          >
            The Queue — Your Daily Plan
          </InfoSectionTitle>
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            Move items you want to consume today into your Queue. Think of it as
            a curated to-do list. Open items to watch, listen, or read, then
            mark them complete when done.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {[
              "Reorder by priority",
              "Mark complete",
              "3–5 items/day ideal",
            ].map((t) => (
              <span
                key={t}
                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-chart-3/10 text-chart-3 border border-chart-3/20"
              >
                {t}
              </span>
            ))}
          </div>
        </InfoSection>

        <InfoSection>
          <InfoSectionTitle
            icon={<BookmarkIcon className="size-4" />}
            iconColor="text-chart-2"
            iconBg="bg-chart-2/12"
          >
            The Backlog — Saved for Later
          </InfoSectionTitle>
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            Not ready for something right now? Save it to your backlog for
            later. Pin important items so they stay at the top. Move them to
            Queue when you're ready.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {[
              <><Pin className="size-2.5 inline" /> Pin items</>,
              <><Search className="size-2.5 inline" /> Search & filter</>,
              <><ArrowRight className="size-2.5 inline" /> Move to Queue</>,
            ].map((t, i) => (
              <span
                key={i}
                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-chart-2/10 text-chart-2 border border-chart-2/20 flex items-center gap-1"
              >
                {t}
              </span>
            ))}
          </div>
        </InfoSection>

        {/* Daily workflow */}
        <InfoSection>
          <InfoSectionTitle
            icon={<Eye className="size-4" />}
            iconColor="text-primary"
            iconBg="bg-primary/12"
          >
            A Typical Day
          </InfoSectionTitle>
          <div className="space-y-3">
            <InfoStep
              number={1}
              icon={<Inbox className="size-4" />}
              color="var(--color-chart-1)"
              title="Morning: Update your feed"
              description="Open the app and tap Update Feed to check your sources for new content."
            />
            <InfoStep
              number={2}
              icon={<Eye className="size-4" />}
              color="var(--color-chart-4)"
              title="Triage: Sort through items"
              description="Queue what you want, save to backlog what looks interesting, dismiss the rest. The Swipe Deck makes this fast on mobile."
            />
            <InfoStep
              number={3}
              icon={<ListChecks className="size-4" />}
              color="var(--color-chart-3)"
              title="Consume: Work through your Queue"
              description="Watch, listen, or read your queued items throughout the day."
            />
            <InfoStep
              number={4}
              icon={<CheckCircle2 className="size-4" />}
              color="var(--color-primary)"
              title="Complete: Mark items done"
              description="Track your progress. Completed items count toward your goals and streaks."
            />
          </div>
        </InfoSection>

        {/* Pro tips */}
        <div className="rounded-xl bg-primary/5 border border-primary/15 p-4">
          <p className="text-xs font-semibold text-primary mb-2">Pro Tips</p>
          <ul className="space-y-1.5 text-xs text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              Keep your Queue small — 3 to 5 items per day is the sweet spot.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              Use the Swipe Deck on mobile for lightning-fast triage.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              Review your Backlog weekly — remove items you'll never get to.
            </li>
          </ul>
        </div>
      </div>
    </FeatureInfoDialog>
  );
}
